import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import {
  Option,
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER,
} from "../../../features/Edit/common";

import { z } from "zod";

// Example of a dynamic form config
export const dynamicFormConfig: TDynamicFormConfig = {
  id: z.number(),
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .transform((val) => val.toUpperCase()),
  isin: z
    .string()
    .refine(
      (val) => val === "" || val === null || val.length === 12,
      "L'ISIN doit comporter  12 caractères",
    )
    .nullable()
    .optional(),
  currency: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  asset_class_2: {
    key_config: {
      validator: {
        asset_class_1: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      },
      primary_key: "asset_class_1",
      secondary_key: "asset_class_2",
    },
    select: {
      real_estate: {
        chateau_lamothe_bergeron: false,
        shareholder_account_chateau: false,
        real_estate_fund: "real_estate_fund",
        direct_real_estate: false,
      },
      saving_account: {
        saving_account: false,
      },
      securitisation_mutual_fund: {
        fct: "fct",
        short_term_bond: "short_term_bond",
        bond: "bond",
      },
      opcvm: {
        equity_fund: false,
        dynamic_money_market_fund: false,
        money_market_eur_fund: false,
        fixed_income_fund: false,
        convertible_bond_fund: false,
      },
      private_equity: {
        private_equity_fund: "private_equity_fund",
        oria_group: "oria_group",
        private_equity_direct: "private_equity_direct",
      },
    },
    section: {
      // Real Estate Fund
      real_estate_fund: {
        investment_date: {
          label: "Date d'investissement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      // fct
      fct: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        coupon_payment_frequency: {
          label: "Fréquence de paiement du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        day_count_basis: {
          label: "Base de de comptage de jour",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Short Term Bond
      short_term_bond: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        coupon_payment_frequency: {
          label: "Fréquence de paiement du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        day_count_basis: {
          label: "Base de de comptage de jour",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Long Term Bond
      bond: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        coupon_payment_frequency: {
          label: "Fréquence de paiement du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        day_count_basis: {
          label: "Base de de comptage de jour",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // PE fund
      private_equity_fund: {
        investment_date: {
          label: "Date d'investissement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Oria group
      oria_group: {
        investment_date: {
          label: "Date d'investissement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Private Equity Direct
      private_equity_direct: {
        investment_date: {
          label: "Date d'investissement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
    },
  },
};

export const DEFAULT_ASSET_CLASS_OPTIONS: Option[] =
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER(dynamicFormConfig);

export const dynamicNewFormConfigWithoutId: Omit<TDynamicFormConfig, "id"> =
  (() => {
    const { id, ...configWithoutId } = dynamicFormConfig;
    return configWithoutId;
  })();
